/**
 * ContractProgression Component
 *
 * Shows every contract belonging to the authenticated client as an originating
 * SOW plus the change orders booked against it, so the client can see what a
 * contract was originally worth, what it grew to, and when that growth happened.
 *
 * Contract value comes from the platform contract chain export because not every
 * change order has been entered into the platform yet. Rows that do resolve to a
 * platform project are marked as linked; rows that do not are still counted so
 * the totals stay accurate.
 *
 * URL Parameters:
 * - ?min_period=MM/DD/YYYY&max_period=MM/DD/YYYY - Limit to change orders booked in a window
 * - ?search=text - Filter by contract, project, or resource name
 * - ?view=all|extended|original - Filter by whether a contract has change orders
 * - ?sort_by=field&sort_dir=asc|desc - Sorting
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DatePicker, Input, Select, Table, Tooltip } from "antd";
import { useAPI } from "../../config/API";
import UserImageCircle from "../../components/UserImageCircle";
import { useAuth } from "../../providers/AuthProvider";

/**
 * Sentinel matching FinancialsController::ClientScopeAll.
 */
const CLIENT_SCOPE_ALL = 'all';

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

/* -------------------------------------------------------------------------- */
/* Formatting helpers                                                          */
/* -------------------------------------------------------------------------- */

const currency = (value, decimals = 0) =>
    `$${Number(value || 0).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;

const compactCurrency = (value) => {
    const amount = Number(value || 0);
    const abs = Math.abs(amount);

    if (abs >= 1000000) return `$${(amount / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
    if (abs >= 1000) return `$${(amount / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;

    return `$${amount.toFixed(0)}`;
};

const formatDate = (value) => (value ? TB.moment(value).format('MMM D, YYYY') : '—');
const formatMonth = (period) => (period ? TB.moment(period + '-01').format("MMM 'YY") : '');

const formatHours = (value) =>
    `${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}h`;

/**
 * Matches the server side normalization so a person is counted once no matter
 * how many roles they held or how their name is spaced.
 */
const normalizeName = (name) => (name || '').toLowerCase().replace(/\s+/g, ' ').trim();

/* -------------------------------------------------------------------------- */
/* Talent                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Wraps children in a link to the talent profile, opened in a new tab so the
 * client does not lose their place in the dashboard.
 *
 * Only resources that resolved to a platform user are linkable; names that came
 * from the contract export with no matching record render as plain content.
 *
 * The click is stopped from bubbling because these sit inside rows that toggle
 * the contract detail on click.
 */
const TalentLink = ({ resource, className = '', children }) => {
    const userId = resource.user?.id;

    if (!userId) {
        return <>{children}</>;
    }

    return (
        <Link
            to={`/talent-profiles/${userId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            title={`View ${resource.name}'s profile`}
            className={className}
        >
            {children}
        </Link>
    );
};

/**
 * Talent avatar, using the shared UserImageCircle so these match avatars
 * everywhere else in the platform: the same initials fallback, the same
 * per-letter background colour, and the same handling of the default profile
 * image.
 *
 * `size` is a pixel number, matching UserImageCircle's own API.
 */
const TalentAvatar = ({ resource, size = 48, linked = true, className = '' }) => {
    // Matched talent arrives already hydrated into the User model, so
    // UserImageCircle can call fullName() for initials and read the model's own
    // talent flags. Resources with no platform record get a label only, which
    // is enough for the initials fallback.
    const user = resource.user || { label: resource.name };

    const avatar = (
        <UserImageCircle
            user={user}
            size={size}
            hideTooltip={true}
            className={className}
        />
    );

    if (!linked || !resource.user?.id) {
        return <span className="inline-block shrink-0">{avatar}</span>;
    }

    return (
        <TalentLink
            resource={resource}
            className="inline-block shrink-0 rounded-full ring-offset-2 hover:ring-2 hover:ring-primary-lighter transition-shadow"
        >
            {avatar}
        </TalentLink>
    );
};

/* -------------------------------------------------------------------------- */
/* Spend over time chart                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Monthly booked spend as stacked bars (original vs change order) with the
 * running lifetime total drawn over the top. Rendered as inline SVG on a fixed
 * viewBox so it scales to whatever width the card gives it.
 */
const SpendOverTimeChart = ({ timeline, palette }) => {
    const [hovered, setHovered] = useState(null);

    const width = 1000;
    const height = 300;
    const padding = { top: 24, right: 64, bottom: 44, left: 64 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;

    const maxMonthly = Math.max(...timeline.map(t => t.fee), 1);
    const maxCumulative = Math.max(...timeline.map(t => t.cumulative_fee), 1);

    const bandWidth = plotWidth / timeline.length;
    const barWidth = Math.max(Math.min(bandWidth * 0.62, 42), 2);

    const bandCenter = (index) => padding.left + bandWidth * index + bandWidth / 2;
    const barY = (value) => padding.top + plotHeight - (value / maxMonthly) * plotHeight;
    const lineY = (value) => padding.top + plotHeight - (value / maxCumulative) * plotHeight;

    const cumulativePath = timeline
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${bandCenter(index)} ${lineY(point.cumulative_fee)}`)
        .join(' ');

    const cumulativeArea = timeline.length
        ? `${cumulativePath} L ${bandCenter(timeline.length - 1)} ${padding.top + plotHeight} L ${bandCenter(0)} ${padding.top + plotHeight} Z`
        : '';

    // Show a readable number of x labels no matter how long the history is.
    const labelInterval = Math.ceil(timeline.length / 12) || 1;
    const gridLines = [0, 0.25, 0.5, 0.75, 1];

    return (
        <div className="relative">
            <svg
                viewBox={`0 0 ${width} ${height}`}
                className="w-full"
                style={{ height: 300 }}
                onMouseLeave={() => setHovered(null)}
            >
                <defs>
                    <linearGradient id="cp-cumulative-fill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.22" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                </defs>

                {/* Horizontal grid + monthly axis */}
                {gridLines.map(ratio => {
                    const y = padding.top + plotHeight - ratio * plotHeight;

                    return (
                        <g key={ratio}>
                            <line
                                x1={padding.left}
                                y1={y}
                                x2={width - padding.right}
                                y2={y}
                                stroke="#e5e7eb"
                                strokeWidth="1"
                                strokeDasharray={ratio === 0 ? '0' : '4 4'}
                            />
                            <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
                                {compactCurrency(maxMonthly * ratio)}
                            </text>
                            <text x={width - padding.right + 10} y={y + 4} textAnchor="start" fontSize="11" fill="#6ee7b7">
                                {compactCurrency(maxCumulative * ratio)}
                            </text>
                        </g>
                    );
                })}

                {/* Cumulative lifetime total */}
                {cumulativeArea && <path d={cumulativeArea} fill="url(#cp-cumulative-fill)" />}
                {cumulativePath && (
                    <path d={cumulativePath} fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinejoin="round" />
                )}

                {timeline.map((point, index) => {
                    const x = bandCenter(index);
                    const originalHeight = padding.top + plotHeight - barY(point.original_fee);
                    const changeHeight = padding.top + plotHeight - barY(point.change_order_fee);
                    const isHovered = hovered === index;

                    return (
                        <g key={point.period}>
                            {/* Original SOW value booked this month */}
                            <rect
                                x={x - barWidth / 2}
                                y={padding.top + plotHeight - originalHeight - changeHeight}
                                width={barWidth}
                                height={Math.max(changeHeight, 0)}
                                fill={isHovered ? palette.coHover : palette.changeOrders[1]}
                                rx="2"
                            />
                            <rect
                                x={x - barWidth / 2}
                                y={padding.top + plotHeight - originalHeight}
                                width={barWidth}
                                height={Math.max(originalHeight, 0)}
                                fill={isHovered ? palette.sowHover : palette.sow}
                                rx="2"
                            />

                            {/* Hover target spanning the whole band */}
                            <rect
                                x={padding.left + bandWidth * index}
                                y={padding.top}
                                width={bandWidth}
                                height={plotHeight}
                                fill="transparent"
                                onMouseEnter={() => setHovered(index)}
                            />

                            {isHovered && (
                                <circle cx={x} cy={lineY(point.cumulative_fee)} r="4.5" fill="#10b981" stroke="#fff" strokeWidth="2" />
                            )}

                            {index % labelInterval === 0 && (
                                <text
                                    x={x}
                                    y={height - padding.bottom + 20}
                                    textAnchor="middle"
                                    fontSize="11"
                                    fill="#9ca3af"
                                >
                                    {formatMonth(point.period)}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>

            {/* Tooltip positioned as a percentage so it tracks the scaled SVG */}
            {hovered !== null && timeline[hovered] && (
                <div
                    className="absolute pointer-events-none z-10 bg-gray-900 text-white rounded-lg shadow-xl px-3 py-2 text-xs whitespace-nowrap"
                    style={{
                        left: `${(bandCenter(hovered) / width) * 100}%`,
                        top: 8,
                        transform: 'translateX(-50%)',
                    }}
                >
                    <div className="font-semibold mb-1">
                        {TB.moment(timeline[hovered].period + '-01').format('MMMM YYYY')}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-sm bg-primary-lighter" />
                        New contracts {currency(timeline[hovered].original_fee)}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-sm bg-secondary-lighter" />
                        Change orders {currency(timeline[hovered].change_order_fee)}
                    </div>
                    <div className="flex items-center gap-2 mt-1 pt-1 border-t border-gray-700">
                        <span className="w-2 h-2 rounded-sm bg-emerald-400" />
                        Lifetime total {currency(timeline[hovered].cumulative_fee)}
                    </div>
                </div>
            )}

            <div className="flex items-center justify-center gap-6 text-xs text-gray-600 mt-2">
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-primary" /> Original contract value
                </span>
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-secondary" /> Change order value
                </span>
                <span className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm bg-emerald-500" /> Cumulative spend
                </span>
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Contract progression bar                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Selectable colour palettes.
 *
 * Temporary testing affordance so the team can compare options on real data
 * before one is settled on. Every palette follows the same two rules: the ramp
 * brightens rather than darkens as change orders accumulate, and it never
 * escalates into red. A change order means the client extended the engagement,
 * so a bar that reddens or grows heavier reads as a warning about overspending
 * when the opposite is true.
 *
 * Class strings are written out in full rather than composed, so Tailwind's
 * scanner picks them all up.
 *
 * Card strings are the seven values SummaryCard destructures:
 * gradient from, gradient to, border, label, value, icon background, icon.
 */
const PALETTES = {
    'wripple-purple': {
        label: 'Wripple Purple',
        hint: 'Brand primary → secondary',
        sow: '#5F3DB1',       // primary.DEFAULT
        sowHover: '#5230A4',  // primary.dark
        coHover: '#8431E3',   // secondary.dark
        changeOrders: ['#913EF0', '#A855F9', '#B764FF', '#C77DFF', '#D481FF', '#EA97FF'],
        connector: 'from-primary to-secondary-lightest',
        accent: 'text-secondary',
        cardOriginal: 'from-violet-50 to-violet-100 border-violet-200 text-primary text-primary-darker bg-violet-200 text-primary',
        cardChange: 'from-purple-50 to-purple-100 border-purple-200 text-secondary text-secondary-darker bg-purple-200 text-secondary',
        badge: 'bg-purple-50 border-purple-200 text-secondary text-secondary-darker',
    },
    'growth-teal': {
        label: 'Growth Teal',
        hint: 'Indigo → teal → green',
        sow: '#4F46E5',
        sowHover: '#4338CA',
        coHover: '#0D9488',
        changeOrders: ['#0EA5E9', '#06B6D4', '#14B8A6', '#10B981', '#22C55E', '#4ADE80'],
        connector: 'from-indigo-500 to-emerald-400',
        accent: 'text-emerald-600',
        cardOriginal: 'from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-600 text-indigo-900 bg-indigo-200 text-indigo-700',
        cardChange: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-600 text-emerald-900 bg-emerald-200 text-emerald-700',
        badge: 'bg-emerald-50 border-emerald-200 text-emerald-700 text-emerald-900',
    },
    'wripple-blue': {
        label: 'Wripple Blue',
        hint: 'Deep blue → brand sky blue',
        sow: '#1D4ED8',
        sowHover: '#1E40AF',
        coHover: '#0284C7',
        changeOrders: ['#4B80E7', '#0090FF', '#38BDF8', '#67CFFF', '#93DBFF', '#BAE6FD'],
        connector: 'from-blue-700 to-sky-200',
        accent: 'text-sky-600',
        cardOriginal: 'from-blue-50 to-blue-100 border-blue-200 text-blue-600 text-blue-900 bg-blue-200 text-blue-700',
        cardChange: 'from-sky-50 to-sky-100 border-sky-200 text-sky-600 text-sky-900 bg-sky-200 text-sky-700',
        badge: 'bg-sky-50 border-sky-200 text-sky-700 text-sky-900',
    },
    'classic-indigo': {
        label: 'Classic Indigo',
        hint: 'Single hue, deep → light',
        sow: '#312E81',
        sowHover: '#3730A3',
        coHover: '#4338CA',
        changeOrders: ['#4338CA', '#4F46E5', '#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE'],
        connector: 'from-indigo-900 to-indigo-200',
        accent: 'text-indigo-600',
        cardOriginal: 'from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-700 text-indigo-900 bg-indigo-200 text-indigo-800',
        cardChange: 'from-slate-50 to-indigo-50 border-indigo-200 text-indigo-500 text-indigo-800 bg-indigo-100 text-indigo-600',
        badge: 'bg-indigo-50 border-indigo-200 text-indigo-600 text-indigo-900',
    },
};

const DEFAULT_PALETTE = 'wripple-purple';

const paletteFor = (key) => PALETTES[key] || PALETTES[DEFAULT_PALETTE];

/**
 * Colour for one step, given the palette and how many change orders precede it.
 */
const stepColor = (palette, step, changeOrderIndex) => (
    step.is_change_order
        ? palette.changeOrders[Math.min(changeOrderIndex, palette.changeOrders.length - 1)]
        : palette.sow
);

/**
 * One horizontal bar per contract, split into the originating SOW and each
 * change order in the order it was booked.
 */
const ProgressionBar = ({ steps, totalFee, palette }) => {
    if (!steps || !steps.length || !totalFee) {
        return <div className="h-2 rounded-full bg-gray-100" />;
    }

    let changeOrderIndex = -1;

    return (
        <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
            {steps.map((step, index) => {
                if (step.is_change_order) changeOrderIndex += 1;

                const width = Math.max((step.fee / totalFee) * 100, 0);
                const color = stepColor(palette, step, changeOrderIndex);

                return (
                    <Tooltip
                        key={step.order_id || index}
                        title={<span>{step.label} — {currency(step.fee)}</span>}
                    >
                        <div
                            style={{
                                width: `${width}%`,
                                backgroundColor: color,
                                // Adjacent brand purples sit close together in
                                // tone, so each step is separated by a hairline
                                // rather than relying on colour alone.
                                boxShadow: index < steps.length - 1 ? 'inset -1.5px 0 0 #fff' : undefined,
                            }}
                            className="h-full transition-opacity hover:opacity-75"
                        />
                    </Tooltip>
                );
            })}
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Contract detail (expanded row)                                              */
/* -------------------------------------------------------------------------- */

/**
 * The full story of one contract: each step as a card on a vertical timeline,
 * with a running total, followed by the people booked across the whole chain.
 */
const ContractDetail = ({ contract, palette }) => {
    const steps = contract.steps || [];
    const maxCumulative = Math.max(...steps.map(s => s.cumulative_fee), 1);

    let changeOrderIndex = -1;

    return (
        <div className="bg-gradient-to-br from-slate-50 to-indigo-50/40 -mx-4 -my-3 px-6 py-6">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                {/* Progression timeline */}
                <div className="xl:col-span-2">
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        Contract Progression
                        <span className="font-normal text-gray-500">
                            ({steps.length} {steps.length === 1 ? 'step' : 'steps'})
                        </span>
                    </h4>

                    <div className="relative">
                        {/* Connector running behind the step markers */}
                        <div className={`absolute left-[15px] top-3 bottom-3 w-0.5 bg-gradient-to-b ${palette.connector}`} />

                        <div className="space-y-3">
                            {steps.map((step, index) => {
                                if (step.is_change_order) changeOrderIndex += 1;

                                const color = stepColor(palette, step, changeOrderIndex);

                                return (
                                    <div key={step.order_id || index} className="relative pl-10">
                                        <div
                                            className="absolute left-0 top-4 w-8 h-8 rounded-full border-4 border-white shadow flex items-center justify-center text-[10px] font-bold text-white"
                                            style={{ backgroundColor: color }}
                                            title={step.label}
                                        >
                                            {step.badge}
                                        </div>

                                        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                                            <div className="flex flex-wrap items-start justify-between gap-3">
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-medium text-gray-900 break-words">
                                                        {step.name}
                                                    </p>
                                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                                                        <span>{formatDate(step.started_at)} → {formatDate(step.ended_at)}</span>
                                                        {step.booked_at && <span>Booked {formatDate(step.booked_at)}</span>}
                                                        {step.fee_type && (
                                                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                                                {step.fee_type}
                                                            </span>
                                                        )}
                                                        {step.hours > 0 && <span>{formatHours(step.hours)}</span>}
                                                    </div>
                                                </div>

                                                <div className="text-right shrink-0">
                                                    <p className="text-lg font-bold" style={{ color }}>
                                                        +{currency(step.fee)}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        Running total {currency(step.cumulative_fee)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Share of the contract's final value reached at this step */}
                                            <div className="mt-3 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                                <div
                                                    className="h-full rounded-full transition-all"
                                                    style={{
                                                        width: `${(step.cumulative_fee / maxCumulative) * 100}%`,
                                                        backgroundColor: color,
                                                    }}
                                                />
                                            </div>

                                            {step.resources && step.resources.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-3">
                                                    {step.resources.map((resource, i) => (
                                                        <TalentLink
                                                            key={`${resource.name}-${i}`}
                                                            resource={resource}
                                                            className={`inline-flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-700 ${
                                                                resource.user?.id ? 'hover:border-indigo-300 hover:bg-indigo-50 transition-colors' : ''
                                                            }`}
                                                        >
                                                            <TalentAvatar
                                                                resource={resource}
                                                                size={28}
                                                                linked={false}
                                                            />
                                                            <span className="font-medium text-gray-800">{resource.name}</span>
                                                            <span className="text-gray-400">· {resource.position}</span>
                                                        </TalentLink>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Team roll-up across the whole chain */}
                <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Team &amp; Spend
                    </h4>

                    <div className="space-y-2">
                        {(contract.resources || []).map((resource, index) => (
                            <div
                                key={`${resource.name}-${index}`}
                                className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3 hover:border-indigo-200 transition-colors"
                            >
                                <TalentAvatar resource={resource} size={56} />

                                <div className="min-w-0 flex-1">
                                    <TalentLink
                                        resource={resource}
                                        className={resource.user?.id
                                            ? 'text-sm font-medium text-indigo-700 hover:text-indigo-900 hover:underline truncate block'
                                            : ''}
                                    >
                                        <p className={`text-sm font-medium truncate ${resource.user?.id ? '' : 'text-gray-900'}`}>
                                            {resource.name}
                                            {resource.user?.id && (
                                                <svg className="inline-block w-3 h-3 ml-1 -mt-0.5 opacity-60" fill="none"
                                                     stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                </svg>
                                            )}
                                        </p>
                                    </TalentLink>
                                    <Tooltip title={resource.role_count > 1 ? resource.position : null}>
                                        <p className="text-xs text-gray-500 truncate">
                                            {resource.position}
                                            {resource.bill_rate ? ` · ${currency(resource.bill_rate)}/hr` : ''}
                                        </p>
                                    </Tooltip>
                                </div>

                                <div className="text-right shrink-0">
                                    <p className="text-sm font-semibold text-gray-900">{currency(resource.fee)}</p>
                                    {resource.hours > 0 && (
                                        <p className="text-xs text-gray-500">{formatHours(resource.hours)}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {!contract.is_linked && (
                        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                            This contract's change orders have not been entered into the platform yet, so
                            the figures above come from the contract records rather than live project data.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Summary cards                                                               */
/* -------------------------------------------------------------------------- */

const SummaryCard = ({ label, value, sub, icon, theme, loading }) => {
    const themes = {
        indigo: 'from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-600 text-indigo-900 bg-indigo-200 text-indigo-700',
        amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-600 text-amber-900 bg-amber-200 text-amber-700',
        // The brand purples carry no 50/100 tints, so the soft backgrounds come
        // from Tailwind's violet and purple scales while the text and icon use
        // the real brand tokens. That keeps the two cards clearly apart: deep
        // primary purple for the original contract, vivid secondary purple for
        // what was added on top.
        primary: 'from-violet-50 to-violet-100 border-violet-200 text-primary text-primary-darker bg-violet-200 text-primary',
        secondary: 'from-purple-50 to-purple-100 border-purple-200 text-secondary text-secondary-darker bg-purple-200 text-secondary',
        emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-600 text-emerald-900 bg-emerald-200 text-emerald-700',
        slate: 'from-slate-50 to-slate-100 border-slate-200 text-slate-600 text-slate-900 bg-slate-200 text-slate-700',
    };

    // `theme` is either a key into the map above or, for the palette driven
    // cards, the seven class names written out directly.
    const [gradFrom, gradTo, border, labelColor, valueColor, iconBg, iconColor] =
        (themes[theme] || theme || themes.indigo).split(' ');

    return (
        <div className={`bg-gradient-to-br ${gradFrom} ${gradTo} rounded-lg p-5 border ${border} relative`}>
            {loading && (
                <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center">
                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-gray-500" />
                </div>
            )}
            <div className="flex items-start justify-between">
                <div className="min-w-0">
                    <p className={`text-sm font-medium ${labelColor} mb-1`}>{label}</p>
                    <p className={`text-3xl font-bold ${valueColor} leading-tight`}>{value}</p>
                    {sub && <p className="text-xs text-gray-600 mt-1">{sub}</p>}
                </div>
                <div className={`${iconBg} rounded-full p-3 shrink-0 ml-3`}>
                    <svg className={`w-6 h-6 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {icon}
                    </svg>
                </div>
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Main view                                                                   */
/* -------------------------------------------------------------------------- */

const ContractProgression = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [expandedKeys, setExpandedKeys] = useState([]);
    const { auth } = useAuth();

    const decodedSearchParams = useMemo(() => {
        return [...searchParams.entries()].reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
    }, [searchParams.toString()]);

    /* ---------------------------------------------------------------------- */
    /* Palette (temporary, for design feedback)                                */
    /* ---------------------------------------------------------------------- */

    const paletteKey = PALETTES[decodedSearchParams.palette] ? decodedSearchParams.palette : DEFAULT_PALETTE;
    const palette = paletteFor(paletteKey);
    const [badgeBg, badgeBorder, badgeLabel, badgeValue] = palette.badge.split(' ');

    /* ---------------------------------------------------------------------- */
    /* Admin client override                                                   */
    /* ---------------------------------------------------------------------- */

    // Temporary tool for staff who can manage clients. The endpoint enforces
    // this independently; hiding the control is only to keep it out of the way
    // of ordinary client users.
    const canManageClients = !!auth?.canManageClients?.();

    const [clientSearch, setClientSearch] = useState('');
    const [debouncedClientSearch, setDebouncedClientSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedClientSearch(clientSearch), 350);

        return () => clearTimeout(timer);
    }, [clientSearch]);

    /**
     * Put the dropdown back to the full client list.
     *
     * Both pieces of state are cleared together: clearing only the input would
     * leave the debounced term driving the query, so the options would stay
     * filtered by a search the user can no longer see.
     */
    const resetClientSearch = () => {
        setClientSearch('');
        setDebouncedClientSearch('');
    };

    const { data: clientsData, isFetching: isFetchingClients } = useAPI(
        ['fetchClients', debouncedClientSearch ? { search: debouncedClientSearch } : {}],
        { enabled: canManageClients }
    );

    // The whole contract history loads by default; the date filter is opt in so
    // the headline lifetime totals are correct on first paint.
    //
    // Freshness is configured in QueryOptions rather than overridden here: this
    // query refetches on every mount and keeps nothing between mounts, so a
    // client never sees figures left over from a previous visit or session.
    const { data, isFetching, error } = useAPI([
        'fetchContractProgression',
        {
            min_period: decodedSearchParams.min_period || undefined,
            max_period: decodedSearchParams.max_period || undefined,
            as_client_id: canManageClients ? (decodedSearchParams.as_client_id || undefined) : undefined,
        }
    ]);

    const contracts = data?.contracts || [];
    const timeline = data?.timeline || [];
    const totals = data?.totals || {};
    const scope = data?.scope || {};

    const isFiltered = Boolean(decodedSearchParams.min_period || decodedSearchParams.max_period);

    // The session client is the default; the server echoes back whichever scope
    // it actually applied, so the control can label the current selection even
    // when that client is not in the latest search results.
    const sessionClientId = data?.client?.id;
    const selectedClientValue = decodedSearchParams.as_client_id
        || (sessionClientId !== undefined ? String(sessionClientId) : undefined);

    const isViewingOtherClient = scope.type === 'client' || scope.type === 'all';
    const showClientColumn = scope.type === 'all';

    const clientOptions = useMemo(() => {
        const options = [{ value: CLIENT_SCOPE_ALL, label: 'All Clients' }];

        if (sessionClientId !== undefined) {
            options.push({
                value: String(sessionClientId),
                label: `${data?.client?.name} (your session)`,
            });
        }

        // Keep the active selection selectable even when a search has narrowed
        // the list away from it.
        if (scope.type === 'client' && scope.client_id !== undefined) {
            options.push({ value: String(scope.client_id), label: scope.client_name });
        }

        (clientsData?.data || []).forEach(client => {
            options.push({ value: String(client.id), label: client.name });
        });

        const seen = new Set();

        return options.filter(option => {
            if (seen.has(option.value)) return false;
            seen.add(option.value);

            return true;
        });
    }, [clientsData, sessionClientId, data?.client?.name, scope.type, scope.client_id, scope.client_name]);

    /* ---------------------------------------------------------------------- */
    /* Client side filtering                                                   */
    /* ---------------------------------------------------------------------- */

    const filteredContracts = useMemo(() => {
        let rows = [...contracts];

        const search = decodedSearchParams.search?.toLowerCase();
        if (search) {
            rows = rows.filter(contract =>
                (contract.name || '').toLowerCase().includes(search)
                || (contract.client_name || '').toLowerCase().includes(search)
                || (contract.project?.name || '').toLowerCase().includes(search)
                || (contract.resources || []).some(resource =>
                    (resource.name || '').toLowerCase().includes(search)
                    || (resource.position || '').toLowerCase().includes(search)
                )
            );
        }

        const view = decodedSearchParams.view;
        if (view === 'extended') {
            rows = rows.filter(contract => contract.change_order_count > 0);
        } else if (view === 'original') {
            rows = rows.filter(contract => contract.change_order_count === 0);
        }

        return rows;
    }, [contracts, decodedSearchParams.search, decodedSearchParams.view]);

    // Totals follow whatever is on screen so the cards and table always agree.
    const visibleTotals = useMemo(() => {
        const isNarrowed = filteredContracts.length !== contracts.length;

        if (!isNarrowed) {
            return { ...totals, is_narrowed: false };
        }

        const originalFee = filteredContracts.reduce((sum, c) => sum + (c.original_fee || 0), 0);
        const changeFee = filteredContracts.reduce((sum, c) => sum + (c.change_order_fee || 0), 0);

        return {
            is_narrowed: true,
            total_fee: originalFee + changeFee,
            original_fee: originalFee,
            change_order_fee: changeFee,
            growth_percent: originalFee > 0 ? Math.round((changeFee / originalFee) * 1000) / 10 : null,
            total_hours: filteredContracts.reduce((sum, c) => sum + (c.total_hours || 0), 0),
            contract_count: filteredContracts.length,
            change_order_count: filteredContracts.reduce((sum, c) => sum + (c.change_order_count || 0), 0),
            extended_count: filteredContracts.filter(c => c.change_order_count > 0).length,
            avg_contract_fee: filteredContracts.length
                ? (originalFee + changeFee) / filteredContracts.length
                : 0,
            // One entry per person across every contract on screen, regardless
            // of how many roles or contracts they appear on.
            resource_count: new Set(
                filteredContracts.flatMap(c => (c.resources || []).map(r => normalizeName(r.name)))
            ).size,
        };
    }, [filteredContracts, contracts, totals]);

    /* ---------------------------------------------------------------------- */
    /* Handlers                                                                */
    /* ---------------------------------------------------------------------- */

    const updateParams = (mutate) => {
        const newParams = new URLSearchParams(searchParams);
        mutate(newParams);
        setSearchParams(newParams);
    };

    const handleSearch = (value) => updateParams(params => {
        value ? params.set('search', value) : params.delete('search');
    });

    const handleViewChange = (value) => updateParams(params => {
        value && value !== 'all' ? params.set('view', value) : params.delete('view');
    });

    const handlePaletteChange = (value) => updateParams(params => {
        // Default stays out of the URL so shared links only carry a palette
        // when one was deliberately chosen.
        value === DEFAULT_PALETTE ? params.delete('palette') : params.set('palette', value);
    });

    const handleClientChange = (value) => updateParams(params => {
        // The session client is the default, so it stays out of the URL.
        if (!value || value === String(sessionClientId)) {
            params.delete('as_client_id');
        } else {
            params.set('as_client_id', value);
        }

        setExpandedKeys([]);
    });

    const handleDateRangeChange = (dates) => updateParams(params => {
        if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
            params.delete('min_period');
            params.delete('max_period');
            return;
        }

        params.set('min_period', dates[0].format('MM/DD/YYYY'));
        params.set('max_period', dates[1].format('MM/DD/YYYY'));
    });

    const clearFilters = () => setSearchParams(new URLSearchParams());

    /* ---------------------------------------------------------------------- */
    /* Table                                                                   */
    /* ---------------------------------------------------------------------- */

    const columns = [
        // Only meaningful once more than one client is in scope.
        ...(showClientColumn ? [{
            title: 'Client',
            dataIndex: 'client_name',
            key: 'client_name',
            width: 180,
            fixed: 'left',
            sorter: (a, b) => (a.client_name || '').localeCompare(b.client_name || ''),
            render: (clientName, record) => (
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        handleClientChange(String(record.client_id ?? ''));
                    }}
                    className="text-left font-medium text-indigo-700 hover:text-indigo-900 hover:underline"
                    title={`Show only ${clientName}`}
                    disabled={!record.client_id}
                >
                    {clientName || '—'}
                </button>
            ),
        }] : []),
        {
            title: 'Contract',
            dataIndex: 'name',
            key: 'name',
            width: 340,
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
            render: (name, record) => (
                <div className="min-w-0">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 break-words">{name}</span>
                        {!record.is_linked && (
                            <Tooltip title="This contract has not been fully entered into the platform yet. Figures come from contract records.">
                                <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                                    UNLINKED
                                </span>
                            </Tooltip>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1 text-xs text-gray-500">
                        {record.fee_type && (
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{record.fee_type}</span>
                        )}
                        <span>{formatDate(record.started_at)} → {formatDate(record.ended_at)}</span>
                        {record.project?.status && (
                            <span className="text-indigo-600">{record.project.status}</span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            title: 'Progression',
            key: 'progression',
            width: 220,
            render: (_, record) => (
                <div>
                    <ProgressionBar steps={record.steps} totalFee={record.total_fee} palette={palette} />
                    <div className="flex items-center justify-between mt-1.5 text-xs text-gray-500">
                        <span>{currency(record.original_fee)} original</span>
                        <span className={record.change_order_count > 0 ? `${palette.accent} font-medium` : ''}>
                            {record.change_order_count > 0
                                ? `+${record.change_order_count} extension${record.change_order_count === 1 ? '' : 's'}`
                                : 'Original scope'}
                        </span>
                    </div>
                </div>
            ),
        },
        {
            title: 'Change Orders',
            dataIndex: 'change_order_fee',
            key: 'change_order_fee',
            width: 160,
            sorter: (a, b) => (a.change_order_fee || 0) - (b.change_order_fee || 0),
            render: (fee, record) => (
                <div>
                    <span className={`font-semibold ${fee > 0 ? palette.accent : 'text-gray-400'}`}>
                        {fee > 0 ? `+${currency(fee)}` : '—'}
                    </span>
                    {record.growth_percent > 0 && (
                        <div className="text-xs text-gray-500 mt-0.5">
                            {record.growth_percent}% expansion
                        </div>
                    )}
                </div>
            ),
        },
        {
            title: 'Total Value',
            dataIndex: 'total_fee',
            key: 'total_fee',
            width: 150,
            defaultSortOrder: 'descend',
            sorter: (a, b) => (a.total_fee || 0) - (b.total_fee || 0),
            render: (fee) => (
                <span className="text-base font-bold text-gray-900">{currency(fee)}</span>
            ),
        },
        {
            title: 'Hours',
            dataIndex: 'total_hours',
            key: 'total_hours',
            width: 110,
            sorter: (a, b) => (a.total_hours || 0) - (b.total_hours || 0),
            render: (hours) => (
                <span className="font-mono text-sm text-gray-700">
                    {hours > 0 ? formatHours(hours) : '—'}
                </span>
            ),
        },
        {
            title: 'Team',
            key: 'team',
            width: 130,
            sorter: (a, b) => (a.resources?.length || 0) - (b.resources?.length || 0),
            render: (_, record) => {
                const resources = record.resources || [];

                return (
                    <div className="flex items-center">
                        {resources.slice(0, 3).map((resource, index) => (
                            <Tooltip
                                key={index}
                                title={`${resource.name} — ${resource.position}${
                                    resource.role_count > 1 ? ` (${resource.role_count} roles)` : ''
                                }${resource.user?.id ? ' · Click to open profile' : ''}`}
                            >
                                <span className="inline-block" style={{ marginLeft: index === 0 ? 0 : -10 }}>
                                    <TalentAvatar
                                        resource={resource}
                                        size={40}
                                        className="border-2 border-white"
                                    />
                                </span>
                            </Tooltip>
                        ))}
                        {resources.length > 3 && (
                            <span className="ml-1 text-xs text-gray-500">+{resources.length - 3}</span>
                        )}
                        {resources.length === 0 && <span className="text-xs text-gray-400">—</span>}
                    </div>
                );
            },
        },
    ];

    /* ---------------------------------------------------------------------- */
    /* States                                                                  */
    /* ---------------------------------------------------------------------- */

    const renderSkeleton = () => (
        <div className="contract-progression">
            <div className="p-6 border-b border-gray-200">
                <div className="h-8 w-72 bg-gray-200 rounded animate-pulse mb-6" />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map(card => (
                        <div key={card} className="bg-gray-100 rounded-lg p-5 border border-gray-200 animate-pulse">
                            <div className="h-4 w-24 bg-gray-300 rounded mb-3" />
                            <div className="h-8 w-32 bg-gray-300 rounded" />
                        </div>
                    ))}
                </div>
            </div>
            <div className="p-6">
                <div className="h-[300px] bg-gray-100 rounded-lg animate-pulse mb-6" />
                <div className="space-y-3">
                    {[1, 2, 3, 4, 5, 6].map(row => (
                        <div key={row} className="h-14 bg-gray-100 rounded animate-pulse" />
                    ))}
                </div>
            </div>
        </div>
    );

    const renderError = () => (
        <div className="p-6">
            <div className="rounded-lg border-2 border-red-200 bg-red-50 p-12">
                <div className="flex flex-col items-center justify-center text-center">
                    <svg className="w-16 h-16 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        We couldn't load your contracts
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md">
                        {error?.message || 'An unexpected error occurred while loading contract data. Please try again.'}
                    </p>
                    <button
                        onClick={clearFilters}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                    >
                        Reset &amp; Retry
                    </button>
                </div>
            </div>
        </div>
    );

    if (isFetching && !data) {
        return renderSkeleton();
    }

    if (error) {
        return (
            <div className="contract-progression">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Contract &amp; Change Order Progression</h2>
                </div>
                {renderError()}
            </div>
        );
    }

    const hasContracts = contracts.length > 0;
    const isEmpty = filteredContracts.length === 0;

    // The client column shifts every summary cell along by one.
    const summaryIndex = (index) => (showClientColumn ? index + 1 : index);

    /* ---------------------------------------------------------------------- */
    /* Render                                                                  */
    /* ---------------------------------------------------------------------- */

    return (
        <div className="contract-progression">

            {/* Header + headline figures */}
            <div className="p-6 border-b border-gray-200">
                <div className="mb-6">
                    <div className="mb-4">
                        <h2 className="text-2xl font-bold text-gray-900">
                            Contract &amp; Change Order Progression
                        </h2>
                        <p className="text-gray-600 text-sm mt-1">
                            {isFiltered
                                ? 'Contract value booked within the selected window.'
                                : 'Total committed value across every contract and the change orders booked against it.'}
                        </p>
                    </div>

                    {/* Testing control on the left, live scope and headline on the right */}
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        {/* Temporary: lets reviewers compare colour options on real data */}
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                Testing · Colour palette
                            </p>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={paletteKey}
                                    onChange={handlePaletteChange}
                                    style={{ width: 190 }}
                                    size="small"
                                >
                                    {Object.entries(PALETTES).map(([key, option]) => (
                                        <Option key={key} value={key} label={option.label}>
                                            <div className="flex items-center justify-between gap-2">
                                                <span>{option.label}</span>
                                                <span className="flex shrink-0">
                                                    {[option.sow, ...option.changeOrders].map((color, index) => (
                                                        <span
                                                            key={index}
                                                            className="w-2 h-3 first:rounded-l-sm last:rounded-r-sm"
                                                            style={{ backgroundColor: color }}
                                                        />
                                                    ))}
                                                </span>
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                                <Tooltip title={palette.hint}>
                                    <span className="flex shrink-0">
                                        {[palette.sow, ...palette.changeOrders].map((color, index) => (
                                            <span
                                                key={index}
                                                className="w-3 h-4 first:rounded-l last:rounded-r"
                                                style={{ backgroundColor: color }}
                                            />
                                        ))}
                                    </span>
                                </Tooltip>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-start justify-end gap-3">
                        {/* Temporary staff tool: view another client's data, or everyone's */}
                        {canManageClients && (
                            <div className="rounded-lg border border-dashed border-indigo-300 bg-indigo-50/60 px-3 py-2">
                                <p className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide mb-1">
                                    Admin · Viewing data for
                                </p>
                                <Select
                                    showSearch
                                    value={selectedClientValue}
                                    onChange={handleClientChange}
                                    onSearch={setClientSearch}
                                    // Tied to the dropdown closing rather than to blur:
                                    // clicking outside the dropdown does not always blur
                                    // the select, which left the list stuck on the last
                                    // search term.
                                    onDropdownVisibleChange={(open) => !open && resetClientSearch()}
                                    onBlur={resetClientSearch}
                                    filterOption={false}
                                    loading={isFetchingClients}
                                    notFoundContent={isFetchingClients ? 'Searching…' : 'No clients found'}
                                    style={{ width: 260 }}
                                    size="small"
                                    placeholder="Select a client"
                                >
                                    {clientOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        )}

                        {visibleTotals.growth_percent > 0 && (
                            <div className={`rounded-lg border px-4 py-2 ${badgeBg} ${badgeBorder}`}>
                                <p className={`text-xs font-medium ${badgeLabel}`}>Engagement expansion</p>
                                <p className={`text-xl font-bold ${badgeValue}`}>
                                    +{visibleTotals.growth_percent}%
                                </p>
                                <p className={`text-[11px] ${badgeLabel}`}>beyond original scope</p>
                            </div>
                        )}
                        </div>
                    </div>
                </div>

                {/* Make it obvious when the figures are not the session client's */}
                {isViewingOtherClient && (
                    <div className="mb-6 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-indigo-900">
                            <span className="font-semibold">
                                {scope.type === 'all'
                                    ? `Showing all clients (${scope.client_count})`
                                    : `Showing ${scope.client_name}`}
                            </span>
                            <span className="text-indigo-700">
                                {' '}— this is not your session organization.
                            </span>
                        </p>
                        <button
                            onClick={() => handleClientChange(String(sessionClientId))}
                            className="text-xs font-medium text-indigo-700 hover:text-indigo-900 underline"
                        >
                            Back to {data?.client?.name}
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                    <SummaryCard
                        theme="emerald"
                        loading={isFetching}
                        label="Total Contract Value"
                        value={currency(visibleTotals.total_fee)}
                        sub={`${visibleTotals.contract_count || 0} contracts · avg ${compactCurrency(visibleTotals.avg_contract_fee)}`}
                        icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />}
                    />
                    <SummaryCard
                        theme={palette.cardOriginal}
                        loading={isFetching}
                        label="Original Contracts"
                        value={currency(visibleTotals.original_fee)}
                        sub="Value at initial signing"
                        icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />}
                    />
                    <SummaryCard
                        theme={palette.cardChange}
                        loading={isFetching}
                        label="Added Through Change Orders"
                        value={currency(visibleTotals.change_order_fee)}
                        sub={`${visibleTotals.change_order_count || 0} COs extending ${visibleTotals.extended_count || 0} contracts`}
                        icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />}
                    />
                    <SummaryCard
                        theme="slate"
                        loading={isFetching}
                        label="Talent Engaged"
                        value={visibleTotals.resource_count || 0}
                        sub={visibleTotals.total_hours > 0 ? `${formatHours(visibleTotals.total_hours)} contracted` : 'Across all contracts'}
                        icon={<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />}
                    />
                </div>
            </div>

            {/* Spend over time - hidden for now. The endpoint still returns the
                timeline and SpendOverTimeChart is still defined above, so
                uncommenting this block is all that is needed to bring it back. */}
            {/*
            {timeline.length > 1 && (
                <div className="px-6 py-6 border-b border-gray-200">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Spend Over Time</h3>
                        <p className="text-sm text-gray-500">
                            Contract value booked each month, with your running lifetime total
                        </p>
                    </div>
                    <SpendOverTimeChart timeline={timeline} palette={palette} />
                </div>
            )}
            */}

            {/* Filters */}
            <div className={`px-6 py-4 bg-gray-50 border-b border-gray-200 transition-opacity ${isFetching ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex-1 min-w-[220px]">
                        <Search
                            placeholder="Search contracts, projects, or talent..."
                            allowClear
                            onSearch={handleSearch}
                            defaultValue={decodedSearchParams.search}
                            className="w-full"
                            disabled={isFetching}
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap font-medium">Show:</span>
                        <Select
                            value={decodedSearchParams.view || 'all'}
                            onChange={handleViewChange}
                            style={{ width: 190 }}
                            disabled={isFetching}
                        >
                            <Option value="all">All contracts</Option>
                            <Option value="extended">With change orders</Option>
                            <Option value="original">No change orders</Option>
                        </Select>
                    </div>

                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap">Booked between:</span>
                        <RangePicker
                            format="MM/DD/YYYY"
                            allowClear
                            onChange={handleDateRangeChange}
                            value={
                                decodedSearchParams.min_period && decodedSearchParams.max_period
                                    ? [
                                        TB.moment(decodedSearchParams.min_period, 'MM/DD/YYYY'),
                                        TB.moment(decodedSearchParams.max_period, 'MM/DD/YYYY'),
                                    ]
                                    : null
                            }
                            style={{ width: 280 }}
                            disabled={isFetching}
                            ranges={{
                                'Year to Date': [TB.moment().startOf('year'), TB.moment()],
                                'Last Quarter': [TB.moment().add(-1, 'quarters').startOf('quarter'), TB.moment().add(-1, 'quarters').endOf('quarter')],
                                'Last Year': [TB.moment().add(-1, 'years').startOf('year'), TB.moment().add(-1, 'years').endOf('year')],
                                'Last 2 Years': [TB.moment().add(-2, 'years').startOf('year'), TB.moment()],
                            }}
                        />
                    </div>
                </div>

                {(decodedSearchParams.search || decodedSearchParams.view || isFiltered) && (
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        <span className="text-xs text-gray-600 font-medium">Active filters:</span>
                        {decodedSearchParams.search && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md border border-blue-200">
                                Search: "{decodedSearchParams.search}"
                            </span>
                        )}
                        {decodedSearchParams.view && decodedSearchParams.view !== 'all' && (
                            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md border border-green-200">
                                {decodedSearchParams.view === 'extended' ? 'With change orders' : 'No change orders'}
                            </span>
                        )}
                        {isFiltered && (
                            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-md border border-amber-200">
                                Booked {decodedSearchParams.min_period} – {decodedSearchParams.max_period}
                            </span>
                        )}
                        <button
                            onClick={clearFilters}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Contracts */}
            {isEmpty ? (
                <div className="p-6">
                    <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12">
                        <div className="flex flex-col items-center justify-center text-center">
                            <svg className="w-20 h-20 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                {hasContracts ? 'No contracts match your filters' : 'No contracts to show yet'}
                            </h3>
                            <p className="text-gray-600 mb-6 max-w-md">
                                {hasContracts
                                    ? 'Try widening your date range or clearing the search to see the rest of your contract history.'
                                    : 'Once contracts are signed for your organization, their value and any change orders will appear here.'}
                            </p>
                            {hasContracts && (
                                <button
                                    onClick={clearFilters}
                                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                                >
                                    Clear All Filters
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="py-6 px-4">
                    {isFetching && (
                        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                            <span className="text-sm text-blue-800 font-medium">Loading contract data...</span>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-1">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredContracts.length}</span>
                            {filteredContracts.length !== contracts.length && (
                                <> of <span className="font-semibold text-gray-900">{contracts.length}</span></>
                            )}
                            {' '}contracts
                        </p>
                        <p className="text-xs text-gray-500">
                            Click any contract to see its full change order history
                        </p>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={filteredContracts}
                        loading={isFetching}
                        rowKey="key"
                        scroll={{ x: showClientColumn ? 1360 : 1180 }}
                        className="shadow-sm"
                        showSorterTooltip={false}
                        rowClassName="cursor-pointer align-top"
                        onRow={(record) => ({
                            onClick: () => setExpandedKeys(keys =>
                                keys.includes(record.key)
                                    ? keys.filter(key => key !== record.key)
                                    : [...keys, record.key]
                            ),
                        })}
                        pagination={{
                            defaultPageSize: 20,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} contracts`,
                        }}
                        expandable={{
                            expandedRowKeys: expandedKeys,
                            onExpandedRowsChange: setExpandedKeys,
                            expandedRowRender: (record) => <ContractDetail contract={record} palette={palette} />,
                            expandRowByClick: false,
                        }}
                        summary={() => (
                            <Table.Summary fixed>
                                <Table.Summary.Row className="bg-gray-50 font-semibold">
                                    {/* Keeps the summary aligned when the client column is present */}
                                    {showClientColumn && (
                                        <Table.Summary.Cell index={0}>
                                            <span className="text-gray-500 text-xs font-normal">
                                                {scope.client_count} clients
                                            </span>
                                        </Table.Summary.Cell>
                                    )}
                                    <Table.Summary.Cell index={summaryIndex(0)}>
                                        <span className="text-gray-900">
                                            {filteredContracts.length} contracts
                                        </span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={summaryIndex(1)}>
                                        <span className="text-gray-500 text-xs font-normal">
                                            {currency(visibleTotals.original_fee)} original
                                        </span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={summaryIndex(2)}>
                                        <span className={palette.accent}>
                                            {visibleTotals.change_order_fee > 0
                                                ? `+${currency(visibleTotals.change_order_fee)}`
                                                : '—'}
                                        </span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={summaryIndex(3)}>
                                        <span className="text-gray-900 text-base">
                                            {currency(visibleTotals.total_fee)}
                                        </span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={summaryIndex(4)}>
                                        <span className="font-mono text-sm text-gray-700">
                                            {formatHours(visibleTotals.total_hours)}
                                        </span>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={summaryIndex(5)} />
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                </div>
            )}
        </div>
    );
};

export default ContractProgression;
